/**
 * Highlight reels — several confirmed clips stitched into ONE MP4 (card §3, plan §6 Phase 3).
 *
 * The whole reel is a single decode→composite→encode pass over the session's footage: the
 * segments are windows of the SAME source video, so they are visited in order and each window's
 * frames are re-based onto a running offset. That gives one continuous timeline (and therefore
 * one audio stream) instead of N files glued together, and it is why the reel can carry its own
 * title bar and the free-tier watermark — every frame goes through compositeFrame exactly once.
 *
 * Audio follows the same one-output rule: AAC re-encode where an AudioEncoder exists, else
 * packet passthrough of the source's own AAC (the Safari shape), with the timestamp offset
 * carried across segment boundaries.
 */
import {
  ALL_FORMATS,
  AudioSampleSink,
  AudioSampleSource,
  BlobSource,
  BufferTarget,
  CanvasSource,
  EncodedAudioPacketSource,
  EncodedPacketSink,
  Input,
  Mp4OutputFormat,
  Output,
  QUALITY_HIGH,
  QUALITY_MEDIUM,
  UrlSource,
  VideoSampleSink,
  type EncodedPacket,
  type InputAudioTrack,
  type InputVideoTrack,
} from 'mediabunny';
import { compositeFrame, loadLogoImage, type OverlayAssets } from '../branding';
import { probeCapabilities, resolveAudioMode } from './probe';
import { pickH264, targetSize } from './webcodecs';
import {
  type AudioTrackMode,
  type CapabilityReport,
  type OverlayConfig,
  type RenderProgress,
} from './types';

export interface ReelSegment {
  clipId: string;
  title: string;
  tIn: number;
  tOut: number;
}

export interface ReelRequest {
  sourceVideo: Blob | string;
  /** Ordered by the coach's selection; the reel's order IS this order. */
  segments: ReelSegment[];
  /** The reel's own title — the title bar on every frame. */
  title: string;
  /** Branding for the frame; its `title` is replaced by the reel title. */
  overlay: OverlayConfig;
  quality: 'preview' | 'export';
  audioTrack?: AudioTrackMode;
  /** Free tier draws "Made with Callout" (card §2). */
  watermark?: boolean;
}

export interface ReelSegmentPlacement {
  clipId: string;
  title: string;
  /** Where this segment starts inside the reel, in seconds. */
  startSeconds: number;
  durationSeconds: number;
  tIn: number;
  tOut: number;
}

export interface ReelResult {
  blob: Blob;
  duration: number;
  sizeBytes: number;
  renderTimeMs: number;
  renderer: 'webcodecs';
  audioTrack: AudioTrackMode;
  videoCodec: string | null;
  width: number;
  height: number;
  segmentCount: number;
  segments: ReelSegmentPlacement[];
  watermark: boolean;
  title: string;
}

export interface ReelOptions {
  onProgress?: (progress: RenderProgress & { segmentIndex: number; segmentCount: number }) => void;
}

export async function renderReel(
  request: ReelRequest,
  options: ReelOptions = {},
): Promise<ReelResult> {
  if (request.segments.length < 2) {
    throw new Error('A highlight reel needs at least 2 segments to stitch');
  }
  for (const segment of request.segments) {
    if (!(segment.tOut > segment.tIn)) {
      throw new Error(`Segment ${segment.clipId} has an empty window`);
    }
  }

  const report: CapabilityReport = await probeCapabilities();
  const videoCodec = pickH264(report);
  if (!videoCodec) throw new Error('No H.264 encoder available in this browser');

  const started = performance.now();
  const input = new Input({
    source:
      typeof request.sourceVideo === 'string'
        ? new UrlSource(request.sourceVideo)
        : new BlobSource(request.sourceVideo),
    formats: ALL_FORMATS,
  });

  let output: Output<Mp4OutputFormat, BufferTarget> | null = null;
  try {
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) throw new Error('Source has no video track');

    const { width, height } = targetSize(
      await videoTrack.getDisplayWidth(),
      await videoTrack.getDisplayHeight(),
      request.quality,
      report,
    );

    const audioTrack = await input.getPrimaryAudioTrack();
    const sourceAudioCodec = audioTrack ? await audioTrack.getCodec() : null;
    const audioMode = audioTrack
      ? resolveAudioMode(report, request.audioTrack, sourceAudioCodec)
      : ('mute' as const);

    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d') as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null;
    if (!ctx) throw new Error('Could not acquire a 2D context for compositing');

    const assets: OverlayAssets = {
      logo: request.overlay.logoUrl
        ? await loadLogoImage(request.overlay.logoUrl).catch(() => null)
        : null,
    };
    const overlay: OverlayConfig = {
      ...request.overlay,
      title: request.title,
      watermark: request.watermark ?? request.overlay.watermark ?? false,
    };

    const videoSource = new CanvasSource(canvas, {
      codec: 'avc',
      fullCodecString: videoCodec,
      quality: request.quality === 'preview' ? QUALITY_MEDIUM : QUALITY_HIGH,
      keyFrameInterval: 2,
    });

    output = new Output({ format: new Mp4OutputFormat(), target: new BufferTarget() });
    output.addVideoTrack(videoSource);

    let audioSource: EncodedAudioPacketSource | null = null;
    let audioReencode: AudioSampleSource | null = null;
    if (audioMode === 'reencode') {
      audioReencode = new AudioSampleSource({
        codec: 'aac',
        quality: request.quality === 'preview' ? QUALITY_MEDIUM : QUALITY_HIGH,
      });
      output.addAudioTrack(audioReencode);
    } else if (audioMode === 'passthrough') {
      audioSource = new EncodedAudioPacketSource('aac');
      output.addAudioTrack(audioSource);
    }

    await output.start();

    const placements: ReelSegmentPlacement[] = [];
    const totalExpected = await estimateTotalFrames(videoTrack, request.segments);
    let offset = 0;
    let frames = 0;

    for (let index = 0; index < request.segments.length; index += 1) {
      const segment = request.segments[index]!;
      const segmentDuration = await encodeSegmentVideo({
        videoTrack,
        videoSource,
        ctx,
        canvas: { width, height },
        overlay,
        assets,
        segment,
        offset,
        onProgress: (frameCount) => {
          frames = frameCount;
          options.onProgress?.({
            frames,
            totalFrames: totalExpected,
            segmentIndex: index,
            segmentCount: request.segments.length,
          });
        },
        framesBefore: frames,
      });

      placements.push({
        clipId: segment.clipId,
        title: segment.title,
        startSeconds: Number(offset.toFixed(3)),
        durationSeconds: Number(segmentDuration.toFixed(3)),
        tIn: segment.tIn,
        tOut: segment.tOut,
      });
      offset += segmentDuration;
    }

    const audioWritten =
      audioReencode !== null
        ? await encodeReelAudio({
            audioTrack: audioTrack as InputAudioTrack,
            source: audioReencode,
            segments: request.segments,
            placements,
          })
        : audioSource !== null
          ? await passthroughReelAudio({
              audioTrack: audioTrack as InputAudioTrack,
              source: audioSource,
              segments: request.segments,
              placements,
            })
          : false;

    videoSource.close();
    audioReencode?.close();
    audioSource?.close();

    await output.finalize();
    const buffer = output.target.buffer;
    if (!buffer) throw new Error('Muxer produced no buffer');
    const blob = new Blob([buffer], { type: 'video/mp4' });

    return {
      blob,
      duration: Number(offset.toFixed(3)),
      sizeBytes: blob.size,
      renderTimeMs: Math.round(performance.now() - started),
      renderer: 'webcodecs',
      audioTrack: audioWritten ? audioMode : 'mute',
      videoCodec,
      width,
      height,
      segmentCount: placements.length,
      segments: placements,
      watermark: overlay.watermark === true,
      title: request.title,
    };
  } finally {
    try {
      input.dispose();
    } catch {
      /* already disposed */
    }
    if (output && !output.target.buffer) {
      try {
        await output.cancel();
      } catch {
        /* nothing to cancel */
      }
    }
  }
}

interface EncodeSegmentArgs {
  videoTrack: InputVideoTrack;
  videoSource: CanvasSource;
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  canvas: { width: number; height: number };
  overlay: OverlayConfig;
  assets: OverlayAssets;
  segment: ReelSegment;
  /** Running timeline position of this segment inside the reel. */
  offset: number;
  framesBefore: number;
  onProgress: (frameCount: number) => void;
}

/** One segment: decode → composite → encode, with every timestamp moved onto the reel timeline. */
async function encodeSegmentVideo(args: EncodeSegmentArgs): Promise<number> {
  const { videoTrack, videoSource, ctx, canvas, overlay, assets, segment, offset } = args;
  const sink = new VideoSampleSink(videoTrack);

  let duration = 0;
  let frames = args.framesBefore;

  for await (const sample of sink.samples(segment.tIn, segment.tOut)) {
    try {
      const local = Math.max(0, sample.timestamp - segment.tIn);
      const frameDuration = sample.duration > 0 ? sample.duration : 1 / 30;
      compositeFrame(ctx, sample.toCanvasImageSource(), overlay, canvas, assets);
      await videoSource.add(offset + local, frameDuration);
      duration = Math.max(duration, local + frameDuration);
      frames += 1;
      args.onProgress(frames);
    } finally {
      sample.close();
    }
  }

  if (duration === 0) throw new Error(`No video frames in segment ${segment.clipId}`);
  return duration;
}

/** Total frames across every segment — the progress bar's denominator. */
async function estimateTotalFrames(
  track: InputVideoTrack,
  segments: ReelSegment[],
): Promise<number | null> {
  try {
    const stats = await track.computePacketStats(40);
    if (!stats || !Number.isFinite(stats.averagePacketRate) || stats.averagePacketRate <= 0) {
      return null;
    }
    const seconds = segments.reduce((total, segment) => total + (segment.tOut - segment.tIn), 0);
    return Math.max(1, Math.round(seconds * stats.averagePacketRate));
  } catch {
    return null;
  }
}

/** Re-encode the reel's audio: segment by segment, each moved onto the reel timeline. */
async function encodeReelAudio(args: {
  audioTrack: InputAudioTrack;
  source: AudioSampleSource;
  segments: ReelSegment[];
  placements: ReelSegmentPlacement[];
}): Promise<boolean> {
  const { audioTrack, source, segments, placements } = args;
  let written = 0;
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index]!;
    const placement = placements[index]!;
    const sink = new AudioSampleSink(audioTrack);
    for await (const sample of sink.samples(segment.tIn, segment.tOut)) {
      try {
        const local = Math.max(0, sample.timestamp - segment.tIn);
        sample.setTimestamp(placement.startSeconds + local);
        await source.add(sample);
        written += 1;
      } finally {
        sample.close();
      }
    }
  }
  return written > 0;
}

/** Packet passthrough for the reel: the source's own AAC frames, re-based per segment. */
async function passthroughReelAudio(args: {
  audioTrack: InputAudioTrack;
  source: EncodedAudioPacketSource;
  segments: ReelSegment[];
  placements: ReelSegmentPlacement[];
}): Promise<boolean> {
  const { audioTrack, source, segments, placements } = args;
  const codec = await audioTrack.getCodec();
  if (codec !== 'aac') return false;
  const decoderConfig = await audioTrack.getDecoderConfig();

  let written = 0;
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index]!;
    const placement = placements[index]!;
    const sink = new EncodedPacketSink(audioTrack);
    const start = await sink.getKeyPacket(segment.tIn);
    const end = await sink.getPacket(segment.tOut);
    for await (const packet of sink.packets(start ?? undefined, end ?? undefined)) {
      if (packet.timestamp + packet.duration <= segment.tIn) continue;
      if (packet.timestamp >= segment.tOut) break;
      const local = Math.max(0, packet.timestamp - segment.tIn);
      const shifted: EncodedPacket = packet.clone({ timestamp: placement.startSeconds + local });
      await source.add(shifted, written === 0 && decoderConfig ? { decoderConfig } : undefined);
      written += 1;
    }
  }
  return written > 0;
}

function createCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/** A filename-safe reel name, so a download is recognisable in a coach's folder. */
export function reelFilename(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `callout-reel-${slug || 'highlights'}.mp4`;
}
